import { useCallback, useEffect, useMemo, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Spinner } from '../../../../../components/Spinner';
import VirtualizedList from '../../../../../components/VirtualizedList';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import moment from 'moment';
import { PAYMENT_LINK_BADGE } from '../../../../../constants/fundings';
import { toast } from 'react-toastify';
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
import NewFundingPopoverNewLinkForm from '../forms/NewFundingPopoverNewLinkForm';

function NewFundingPaymentLinksTab({
  fundingId,
  fundingPaymentLinks,
  fundingOutstandingAmount,
  refreshTab,
  setRefreshTab,
  spinner,
  setSpinner,
  refreshFundingData,
}) {
  const { apiOriginFunding } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [loading, setLoading] = useState(false);

  const getPaymentLinks = useCallback(async () => {
    setSpinner(true);
    await refreshFundingData();
    setSpinner(false);
  }, [refreshFundingData, setSpinner]);

  useEffect(() => {
    if (refreshTab === 'payment_links') {
      getPaymentLinks();
      setRefreshTab('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTab]);

  const expireLink = useCallback(
    async linkId => {
      setLoading(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginFunding}/ad_hoc_payment/expire_link`,
        method: 'POST',
        data: JSON.stringify({ fundingId, linkId }),
      };

      try {
        await makeRequest(config);
        await refreshFundingData();

        toast.success('Link expired successfully');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [apiOriginFunding, getAccessTokenSilently, refreshFundingData, fundingId],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: 'createTimestamp',
        header: 'Created at',
        width: 240,
        renderValue: value =>
          value ? moment.utc(value).local().format('MMM DD, YYYY H:mm:ss (UTCZ)') : '---',
      },
      {
        key: 'user',
        header: 'Created by',
        width: 240,
      },
      {
        key: 'amount',
        header: 'Amount',
        width: 100,
        renderValue: value =>
          value !== null && value !== undefined
            ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
            : '---',
      },
      {
        key: 'allowAmountChange',
        header: 'Allow amount change',
        width: 180,
        renderValue: value => (value ? 'Yes' : 'No'),
      },
      {
        key: 'minimalAmount',
        header: 'Minimal Amount',
        width: 140,
        renderValue: value =>
          value !== null && value !== undefined
            ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
            : '---',
      },
      {
        key: 'status',
        header: 'Status',
        width: 180,
        renderValue: (value, row) => (
          <>
            <Badge className="bo-common-inline-badge" bg={PAYMENT_LINK_BADGE[value]?.color}>
              {PAYMENT_LINK_BADGE[value]?.text}
            </Badge>
            {row.paymentByAdmin && (
              <Badge className="bo-common-inline-badge ms-1" bg="danger">
                Payment By Admin
              </Badge>
            )}
          </>
        ),
      },
      {
        key: 'copy',
        header: '',
        width: 100,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            onClick={() => {
              navigator.clipboard.writeText(row.url);
              toast.success('URL copied to clipboard');
            }}
          >
            Copy Link
          </Button>
        ),
      },
      {
        key: 'expire',
        header: '',
        width: 100,
        renderValue: (_, row) => (
          <Button className="bo-common-inline-button" onClick={() => expireLink(row.id)}>
            Expire
          </Button>
        ),
      },
    ],
    [expireLink],
  );

  return (
    <Spinner visible={spinner || loading} wrapperStyle={{ height: '100%' }}>
      <div className="h-100">
        <div className="p-3 d-flex gap-2 overflow-hidden">
          <div className="d-flex align-items-center">
            <NewFundingPopoverNewLinkForm
              fundingId={fundingId}
              fundingOutstandingAmount={fundingOutstandingAmount}
              refreshFundingData={refreshFundingData}
            />
          </div>
        </div>
        <AutoSizer>
          {({ height, width }) => (
            <VirtualizedList
              tableRows={fundingPaymentLinks}
              tableColumns={tableColumns}
              width={width}
              height={height - 58}
              headerTopBorder
              rowKey="createTimestamp"
            />
          )}
        </AutoSizer>
      </div>
    </Spinner>
  );
}

export default NewFundingPaymentLinksTab;
